export type CategoryRubberCountInput = {
  isActive?: boolean;
  numberOfMatches?: number | null;
};

/** Literal match count for one category. 0 / empty / null / non-finite → 0. Fractions are floored. */
export function categoryRubberCount(numberOfMatches?: number | null | string): number {
  const n = Number(numberOfMatches);
  if (!Number.isFinite(n) || n <= 0) {
    return 0;
  }
  return Math.floor(n);
}

/** Count is the source of truth: any positive rubber count means the category is on. */
export function isCategoryActiveFromCount(numberOfMatches?: number | null | string): boolean {
  return categoryRubberCount(numberOfMatches) > 0;
}

export function countActiveCategoryRubbers(categories: CategoryRubberCountInput[]): number {
  let total = 0;
  for (const cat of categories) {
    if (cat.isActive === false) continue;
    total += categoryRubberCount(cat.numberOfMatches);
  }
  return total;
}

export function isEvenRubberCount(count: number): boolean {
  return count > 0 && count % 2 === 0;
}

export function shouldOfferTiebreakerForClass(categories: CategoryRubberCountInput[]): boolean {
  return isEvenRubberCount(countActiveCategoryRubbers(categories));
}

export type CategorySlotInput = CategoryRubberCountInput & {
  id?: number | null;
};

export type TeamDeciderFormAdjustment = {
  tiebreakerEnabled?: boolean;
  groupTiebreakerEnabled?: boolean;
};

export type TeamRecipeCountChangeResult<T extends CategoryRubberCountInput> = {
  tournamentCategories: T[];
  teamMatchupSlotOrder: number[];
} & TeamDeciderFormAdjustment;

/**
 * When the decider becomes available (even rubber count), turn elimination on.
 * When the count is odd and greater than 0, turn both stage flags off.
 * First paint keeps a saved No while the decider can be offered, and clears a saved Yes
 * when it cannot.
 */
export function teamDeciderFormAdjustment(input: {
  canOfferTiebreaker: boolean;
  previouslyOffered: boolean | null;
  activeRubberCount: number;
  tiebreakerEnabled: boolean;
  groupTiebreakerEnabled: boolean;
}): TeamDeciderFormAdjustment | null {
  if (input.previouslyOffered === null) {
    if (
      !input.canOfferTiebreaker &&
      input.activeRubberCount > 0 &&
      (input.tiebreakerEnabled || input.groupTiebreakerEnabled)
    ) {
      return { tiebreakerEnabled: false, groupTiebreakerEnabled: false };
    }
    return null;
  }
  if (input.canOfferTiebreaker && !input.previouslyOffered) {
    return { tiebreakerEnabled: true };
  }
  if (
    !input.canOfferTiebreaker &&
    input.activeRubberCount > 0 &&
    (input.tiebreakerEnabled || input.groupTiebreakerEnabled)
  ) {
    return { tiebreakerEnabled: false, groupTiebreakerEnabled: false };
  }
  return null;
}

export type TeamMatchupSlot = {
  categoryIndex: number;
};

function clampMatchCount(nextCount: number): number {
  if (!Number.isFinite(nextCount) || nextCount <= 0) {
    return 0;
  }
  return Math.floor(nextCount);
}

function withCountAt<T extends CategoryRubberCountInput>(
  categories: T[],
  index: number,
  nextCount: number
): T[] {
  const clamped = clampMatchCount(nextCount);
  return categories.map((cat, i) =>
    i === index
      ? {
          ...cat,
          numberOfMatches: clamped,
          isActive: isCategoryActiveFromCount(clamped),
        }
      : cat
  );
}

function commitRecipeCategories<T extends CategorySlotInput>(
  previous: T[],
  next: T[],
  nextOrder: number[],
  flags: { tiebreakerEnabled: boolean; groupTiebreakerEnabled: boolean }
): TeamRecipeCountChangeResult<T> {
  const adjustment = teamDeciderFormAdjustment({
    canOfferTiebreaker: shouldOfferTiebreakerForClass(next),
    previouslyOffered: shouldOfferTiebreakerForClass(previous),
    activeRubberCount: countActiveCategoryRubbers(next),
    tiebreakerEnabled: flags.tiebreakerEnabled,
    groupTiebreakerEnabled: flags.groupTiebreakerEnabled,
  });
  return {
    tournamentCategories: next,
    teamMatchupSlotOrder: nextOrder,
    ...(adjustment ?? {}),
  };
}

function categorySlotKey<T extends CategorySlotInput>(cat: T, index: number): number {
  return typeof cat.id === 'number' ? cat.id : index;
}

function parseSlotOrder(slotOrder: unknown): number[] | null {
  if (slotOrder == null) {
    return null;
  }
  let raw: unknown = slotOrder;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(raw) || raw.length === 0) {
    return null;
  }
  const ids = raw.map((value) => Number(value));
  if (ids.some((n) => !Number.isFinite(n))) {
    return null;
  }
  return ids;
}

function countKeys(ids: number[]): Map<number, number> {
  const counts = new Map<number, number>();
  for (const id of ids) {
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

/** Category IDs (or indexes when id is missing) grouped by category-list order. */
export function groupedTeamMatchupSlotOrder<T extends CategorySlotInput>(categories: T[]): number[] {
  const order: number[] = [];
  categories.forEach((cat, index) => {
    if (cat.isActive === false) {
      return;
    }
    const n = categoryRubberCount(cat.numberOfMatches);
    const key = categorySlotKey(cat, index);
    for (let i = 0; i < n; i++) {
      order.push(key);
    }
  });
  return order;
}

export function isValidTeamMatchupSlotOrder<T extends CategorySlotInput>(
  categories: T[],
  slotOrder: unknown
): boolean {
  const parsed = parseSlotOrder(slotOrder);
  if (!parsed) {
    return false;
  }
  const expected = groupedTeamMatchupSlotOrder(categories);
  if (parsed.length !== expected.length) {
    return false;
  }
  const a = countKeys(parsed);
  const b = countKeys(expected);
  if (a.size !== b.size) {
    return false;
  }
  for (const [key, count] of a) {
    if (b.get(key) !== count) {
      return false;
    }
  }
  return true;
}

export function materializeTeamMatchupSlotOrder<T extends CategorySlotInput>(
  categories: T[],
  slotOrder?: unknown
): number[] {
  if (isValidTeamMatchupSlotOrder(categories, slotOrder)) {
    return [...parseSlotOrder(slotOrder)!];
  }
  return groupedTeamMatchupSlotOrder(categories);
}

function indexBySlotKey<T extends CategorySlotInput>(categories: T[]): Map<number, number> {
  const map = new Map<number, number>();
  categories.forEach((cat, index) => {
    map.set(categorySlotKey(cat, index), index);
  });
  return map;
}

function categoriesFromSlotOrder<T extends CategorySlotInput>(categories: T[], slotOrder: number[]): T[] {
  const counts = countKeys(slotOrder);
  return categories.map((cat, index) => {
    const n = counts.get(categorySlotKey(cat, index)) ?? 0;
    return {
      ...cat,
      numberOfMatches: n,
      isActive: isCategoryActiveFromCount(n),
    };
  });
}

/** Expand each category's match count into numbered slots. Uses saved order when valid. */
export function expandTeamMatchupSlots<T extends CategorySlotInput>(
  categories: T[],
  slotOrder?: unknown
): TeamMatchupSlot[] {
  const order = materializeTeamMatchupSlotOrder(categories, slotOrder);
  const byKey = indexBySlotKey(categories);
  return order
    .map((key) => ({ categoryIndex: byKey.get(key) ?? -1 }))
    .filter((slot) => slot.categoryIndex >= 0);
}

/** Categories repeated in generate order. Invalid/null order uses grouped expansion. */
export function resolveTeamMatchupCategorySequence<T extends CategorySlotInput>(
  categories: T[],
  slotOrder?: unknown
): T[] {
  const order = materializeTeamMatchupSlotOrder(categories, slotOrder);
  const byKey = new Map<number, T>();
  categories.forEach((cat, index) => {
    byKey.set(categorySlotKey(cat, index), cat);
  });
  return order.map((key) => byKey.get(key)).filter((cat): cat is T => cat != null);
}

/**
 * Next recipe counts plus any auto-defaulted stage flags, so count and decider
 * can be written in one Formik update.
 */
export function applyTeamRecipeCountChange<T extends CategorySlotInput>(
  categories: T[],
  index: number,
  nextCount: number,
  flags: { tiebreakerEnabled: boolean; groupTiebreakerEnabled: boolean },
  slotOrder?: unknown
): TeamRecipeCountChangeResult<T> {
  if (index < 0 || index >= categories.length) {
    return {
      tournamentCategories: categories,
      teamMatchupSlotOrder: materializeTeamMatchupSlotOrder(categories, slotOrder),
    };
  }
  const key = categorySlotKey(categories[index], index);
  const current = categoryRubberCount(categories[index].numberOfMatches);
  const target = clampMatchCount(nextCount);
  let order = materializeTeamMatchupSlotOrder(categories, slotOrder);
  if (target > current) {
    for (let i = 0; i < target - current; i++) {
      order.push(key);
    }
  } else if (target < current) {
    let remaining = current - target;
    for (let i = order.length - 1; i >= 0 && remaining > 0; i--) {
      if (order[i] === key) {
        order.splice(i, 1);
        remaining -= 1;
      }
    }
  }
  const next = categoriesFromSlotOrder(withCountAt(categories, index, target), order);
  return commitRecipeCategories(categories, next, order, flags);
}

export function applyTeamMatchupSlotAdd<T extends CategorySlotInput>(
  categories: T[],
  categoryIndex: number,
  flags: { tiebreakerEnabled: boolean; groupTiebreakerEnabled: boolean },
  slotOrder?: unknown
): TeamRecipeCountChangeResult<T> {
  if (categoryIndex < 0 || categoryIndex >= categories.length) {
    return {
      tournamentCategories: categories,
      teamMatchupSlotOrder: materializeTeamMatchupSlotOrder(categories, slotOrder),
    };
  }
  const order = materializeTeamMatchupSlotOrder(categories, slotOrder);
  order.push(categorySlotKey(categories[categoryIndex], categoryIndex));
  const next = categoriesFromSlotOrder(categories, order);
  return commitRecipeCategories(categories, next, order, flags);
}

export function applyTeamMatchupSlotRemove<T extends CategorySlotInput>(
  categories: T[],
  slotIndex: number,
  flags: { tiebreakerEnabled: boolean; groupTiebreakerEnabled: boolean },
  slotOrder?: unknown
): TeamRecipeCountChangeResult<T> {
  const order = materializeTeamMatchupSlotOrder(categories, slotOrder);
  if (slotIndex < 0 || slotIndex >= order.length) {
    return { tournamentCategories: categories, teamMatchupSlotOrder: order };
  }
  order.splice(slotIndex, 1);
  const next = categoriesFromSlotOrder(categories, order);
  return commitRecipeCategories(categories, next, order, flags);
}

export function applyTeamMatchupSlotChange<T extends CategorySlotInput>(
  categories: T[],
  slotIndex: number,
  toCategoryIndex: number,
  flags: { tiebreakerEnabled: boolean; groupTiebreakerEnabled: boolean },
  slotOrder?: unknown
): TeamRecipeCountChangeResult<T> {
  const order = materializeTeamMatchupSlotOrder(categories, slotOrder);
  if (
    slotIndex < 0 ||
    slotIndex >= order.length ||
    toCategoryIndex < 0 ||
    toCategoryIndex >= categories.length
  ) {
    return { tournamentCategories: categories, teamMatchupSlotOrder: order };
  }
  const nextKey = categorySlotKey(categories[toCategoryIndex], toCategoryIndex);
  if (order[slotIndex] === nextKey) {
    return { tournamentCategories: categories, teamMatchupSlotOrder: order };
  }
  order[slotIndex] = nextKey;
  const next = categoriesFromSlotOrder(categories, order);
  return commitRecipeCategories(categories, next, order, flags);
}

export function applyTeamMatchupSlotReorder<T extends CategorySlotInput>(
  categories: T[],
  fromSlotIndex: number,
  toSlotIndex: number,
  flags: { tiebreakerEnabled: boolean; groupTiebreakerEnabled: boolean },
  slotOrder?: unknown
): TeamRecipeCountChangeResult<T> {
  const order = materializeTeamMatchupSlotOrder(categories, slotOrder);
  if (
    fromSlotIndex < 0 ||
    toSlotIndex < 0 ||
    fromSlotIndex >= order.length ||
    toSlotIndex >= order.length ||
    fromSlotIndex === toSlotIndex
  ) {
    return { tournamentCategories: categories, teamMatchupSlotOrder: order };
  }
  const [moved] = order.splice(fromSlotIndex, 1);
  order.splice(toSlotIndex, 0, moved);
  return commitRecipeCategories(categories, categories, order, flags);
}
